import { useEffect, useMemo, useRef } from 'react'
import { Alert, Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import type { Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { tenantToday } from '@/shared/lib/dayRange'
import {
  EXPENSE_TYPES,
  PAYMENT_SOURCE_TYPES,
  costCenterDepths,
  fetchAccounts,
  fetchCostCenters,
  isPeriodClosed,
  recordExpense,
  type Account,
  type JournalEntry,
  type Period,
} from './financeApi'
import { periodForDate } from './periodFormat'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface ExpenseModalProps {
  open: boolean
  /**
   * Davrlar — "bu sana uchun ochiq davr bormi" ni OLDINDAN aytish uchun.
   *
   * ⚠️ `null` = ma'lum emas. Faqat `finance.period.view` huquqi bor
   * operator bu ro'yxatni oladi; qolganida hech qanday xulosa
   * chiqarilmaydi va ogohlantirish CHIZILMAYDI. Bo'sh ro'yxatdan
   * "davr yo'q" degan xulosa chiqarish yiqilgan so'rovni javob deb
   * o'qish bo'lardi (`guards.mjs` 3-bo'lim).
   */
  periods: readonly Period[] | null
  onClose: () => void
  /** ⚠️ Natijani SAHIFA ko'rsatadi — u oyna yopilgandan keyin ham o'qiladi */
  onDone: (entry: JournalEntry) => void
}

interface FormValues {
  entryDate: Dayjs
  expenseAccountId: string
  creditAccountId: string
  orgNodeId?: string | null
  amount?: Money | null
  memo?: string
}

const MEMO_MAX = 500

/**
 * Ro'yxatdagi bitta qavat uchun chekinish.
 *
 * ⚠️ UZILMAS probel (U+00A0), oddiy probel EMAS: HTML ketma-ket
 * probellarni BITTAGA qisqartiradi va daraxt qavatlari ekranda
 * yo'qolardi — ro'yxat tekis bo'lib ko'rinardi.
 *
 * ⚠️ Belgi `\u00a0` shaklida yozilgan, XOM belgi sifatida emas: xom
 * uzilmas probel oddiy probeldan ko'zga farq qilmaydi va keyingi
 * tahrirlovchi uni bilmasdan "tuzatib" qo'yardi.
 */
const INDENT = '\u00a0\u00a0'

/**
 * Xarajat kiritish — ijara, elektr, transport.
 *
 * <h2>⚠️ IKKI HISOB SO'RALADI, BITTA EMAS</h2>
 * "Xarajat" degan yozuvning O'ZI ikki yozuvli buxgalteriyada mavjud
 * emas: pul QAYERDAN chiqqani ham ko'rsatilishi shart — bankdan,
 * kassadan, yoki hali to'lanmagan bo'lsa yetkazib beruvchiga
 * majburiyat. Faqat xarajat hisobini so'rash "muvozanatni o'zimiz
 * to'ldiramiz" degani bo'lardi va u ALLAQACHON tanlangan hisobga
 * jimgina yozilardi (`ExpenseDraft` javadoc'i).
 *
 * <h2>⚠️ TAKRORIY SO'ROV KALITI OYNA OCHILGANDA BIR MARTA YASALADI</h2>
 * `QARORLAR.md` §20.9-D. "Saqlash" qayta bosilganda kalit O'ZGARMAYDI —
 * aks holda mexanizm bor bo'la turib hech narsani to'smasdi va tarmoq
 * uzilib qayta urinilganda ijara IKKI MARTA yozilardi. Naqsh
 * `StockMovementModal` dan.
 *
 * <p>⚠️ Bu davr yopishdan FARQ QILADI va farq ONGLI: davrning tabiiy
 * kaliti bor (davrning o'zi), xarajatniki YO'Q — bir kunda bir xil
 * summadagi ikki ijara to'lovi ODATIY hol, ya'ni server "o'xshash"
 * so'rovni tanib ololmaydi. Buni faqat klient biladi.
 *
 * <h2>⚠️ XARAJAT MARKAZI — IMKONIYAT BO'YICHA (§19.3)</h2>
 * Ro'yxat `/api/finance/cost-centers` dan keladi va u yerda filtr
 * `is_cost_center` ustuni bo'yicha. Bu oynada `kind === 'BRANCH'` kabi
 * solishtirish YO'Q: administrator "Mintaqa" turini ochib unga xarajat
 * markazi imkoniyatini bersa, ro'yxat darhol uni ham ko'rsatadi.
 *
 * <p>⚠️ Ro'yxat BO'SH bo'lishi mumkin — bugungi holatda barcha tugunlar
 * `AGENT`/`RESELLER` (`CostCenterController` javadoc'i). Bu NUQSON
 * EMAS: bo'sh ro'yxatga "xato" emas, "tashkiliy tuzilmaga filial
 * qo'shing" deb javob beriladi (`QARORLAR.md` §12.5).
 *
 * <h2>⚠️ XATO OYNANING ICHIDA QOLADI</h2>
 * `VoucherVoidModal` naqshi. Bu yerda ayniqsa qimmat: serverning matni
 * ("Bu sana uchun OCHIQ davr yo'q: 2026-09-14") operator SANANI
 * tuzatishi uchun kerak, ya'ni u forma bilan bir ekranda turishi shart.
 */
export function ExpenseModal({ open, periods, onClose, onDone }: ExpenseModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  /** ⚠️ Oyna ochilganda BIR MARTA — qayta urinishda o'zgarmaydi */
  const idempotencyKey = useRef<string>(newIdempotencyKey('expense'))

  useEffect(() => {
    if (!open) return
    idempotencyKey.current = newIdempotencyKey('expense')
    form.setFieldsValue({
      // ⚠️ TENANT kalendaridagi bugun: `dayjs()` brauzernikini olardi va
      // yarim tundan keyingi yarim soatda forma ERTANGI sanani qo'yardi —
      // `periodForDate` esa uni boshqa moliyaviy davrga tushirardi.
      entryDate: tenantToday(),
      expenseAccountId: undefined,
      creditAccountId: undefined,
      orgNodeId: null,
      amount: null,
      memo: '',
    })
  }, [open, form])

  // ⚠️ `activeOnly: true` — o'chirilgan hisob tanlanmasin. Server ham rad
  // etadi (`finance.entry_line_guard()`), lekin ro'yxatda ko'rinib
  // turgan, ammo tanlab bo'lmaydigan qator operatorni chalg'itardi.
  const accounts = useQuery({
    queryKey: queryKeys.finance.accounts(true),
    queryFn: ({ signal }) => fetchAccounts(true, signal),
    enabled: open,
  })

  const costCenters = useQuery({
    queryKey: queryKeys.finance.costCenters,
    queryFn: ({ signal }) => fetchCostCenters(signal),
    enabled: open,
  })

  /**
   * ⚠️ "Ma'lumot bor" xulosasi FAQAT `isSuccess` dan (`guards.mjs`
   * 3-bo'lim). Yiqilgan so'rovda ro'yxat bo'sh bo'ladi va usiz oyna
   * "hisoblar rejasi bo'sh" degan ISHONCHLI javob bo'lib ko'rinardi —
   * holbuki hech narsa o'qilmagan.
   */
  const accountsReliable = accounts.isSuccess
  const centersReliable = costCenters.isSuccess

  const postable = useMemo(
    () => (accounts.data ?? []).filter((account) => account.postable && account.active),
    [accounts.data],
  )

  const optionsOf = (types: readonly string[]) =>
    postable
      .filter((account) => types.includes(account.type))
      .map((account) => ({
        value: account.id,
        label: `${account.code} · ${seededName('accounts', account.code, account.name, t)}`,
      }))

  const expenseOptions = optionsOf(EXPENSE_TYPES)
  const creditOptions = optionsOf(PAYMENT_SOURCE_TYPES)

  const centerOptions = useMemo(() => {
    const rows = costCenters.data ?? []
    // ⚠️ Iyerarxiya `path` dan — `parentId` bo'ylab yurilmaydi
    // (§20.4: "daraxt bitta egada"). `SUBTREE` doirasida ildizning
    // otasi ro'yxatda BO'LMAYDI va zanjir bo'ylab yurgan kod o'sha
    // yerda uzilardi.
    const depths = costCenterDepths(rows)
    return rows.map((node) => ({
      value: node.id,
      // ⚠️ UZILMAS probel (U+00A0), oddiy probel EMAS: HTML ketma-ket
      // probellarni BITTAGA qisqartiradi va qavatlar ekranda yo'qolardi
      // — ro'yxat tekis bo'lib ko'rinardi.
      label: `${INDENT.repeat(depths.get(node.id) ?? 0)}${node.displayName} · ${node.code}`,
    }))
  }, [costCenters.data])

  const expenseAccountId = Form.useWatch('expenseAccountId', form)
  const entryDate = Form.useWatch('entryDate', form)

  const selectedExpense: Account | null =
    postable.find((account) => account.id === expenseAccountId) ?? null

  /** ⚠️ Bu hisobda o'lchov MAJBURIY — bazadagi `entry_line_guard()` rad etadi */
  const dimensionRequired = selectedExpense?.requiresDimension === true

  /**
   * Sana uchun OCHIQ davr bormi.
   *
   * `null` — bilmaymiz (davrlar ro'yxati yo'q yoki sana tanlanmagan);
   * bunday holatda hech narsa aytilmaydi. Server baribir tekshiradi va
   * aniq matn bilan rad etadi.
   */
  const periodProblem = useMemo(() => {
    if (periods === null || !entryDate) return null
    const found = periodForDate(periods, entryDate.format('YYYY-MM-DD'))
    if (found === null) return 'missing' as const
    return isPeriodClosed(found) ? ('closed' as const) : null
  }, [periods, entryDate])

  const submit = useMutation({
    mutationFn: (values: FormValues) =>
      recordExpense(
        {
          entryDate: values.entryDate.format('YYYY-MM-DD'),
          expenseAccountId: values.expenseAccountId,
          creditAccountId: values.creditAccountId,
          orgNodeId: values.orgNodeId ?? null,
          // ⚠️ MINOR birlik va MUSBAT son. Ishorani SERVIS qo'yadi —
          // klientga qoldirish "manfiy xarajat" yozuvlariga yo'l ochardi.
          amountMinor: values.amount?.amount ?? 0,
          memo: values.memo?.trim() ? values.memo.trim() : null,
        },
        idempotencyKey.current,
      ),
    onSuccess: (entry) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      form.resetFields()
      onDone(entry)
      onClose()
    },
  })

  /** Bekor qilib chiqish — eski xato xabari keyingi ochilishda chiqmasin */
  const close = () => {
    submit.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('finance.expense.title')}
      okText={t('finance.expense.submit')}
      cancelText={t('common.cancel')}
      confirmLoading={submit.isPending}
      /*
        ⚠️ DAVR MUAMMOSI BO'LSA — TUGMA O'CHIQ.

        Ilgari faqat `accountsReliable` tekshirilardi va yopiq davrda
        so'rov BARIBIR ketardi. Jonli o'lchandi (2026-08-16, `af-isp`,
        panel ingliz tilida): server `409` qaytardi va ekranda IKKI
        xabar yonma-yon turardi —

          "Conflicts with the current state — Bu sana uchun OCHIQ davr
           yo'q: 2026-08-16 (davr ochilmaganmi yoki allaqachon
           yopilganmi?)"
          "The period covering this date is closed. Pick another date,
           or open a new period."

        Birinchisi server tilida, ichki atama bilan va savol shaklida;
        ikkinchisi — aynan kerakli javob. Ikkalasini birga ko'rsatish
        operatorni "qaysi biriga ishonay?" holatiga qo'yardi.

        Endi so'rov umuman ketmaydi va faqat ikkinchi xabar qoladi.
        Server tekshiruvi joyida turadi (sana chegarasi TENANT
        zonasida hisoblanadi — panel taxmini yagona qorovul emas).
      */
      okButtonProps={{ disabled: !accountsReliable || periodProblem !== null }}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => submit.mutate(values))}
      destroyOnHidden
      width={720}
    >
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={submit.isPending}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.expense.note')}
          description={t('finance.expense.branchCashHint')}
        />

        {/* Serverning matni ("Bu sana uchun OCHIQ davr yo'q: ...") SHU
            YERDA qoladi — u operatorga keyingi qadamni ko'rsatadi */}
        {submit.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(submit.error, t)}
          />
        ) : null}

        {/* ⚠️ Hisoblar o'qilmagan bo'lsa forma YOLG'ON ko'rinardi: bo'sh
            tanlov "hisob yo'q" degan xulosaga olib kelardi */}
        {accounts.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(accounts.error, t)}
          />
        ) : null}

        {periodProblem !== null ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              periodProblem === 'closed'
                ? t('finance.expense.periodClosed')
                : t('finance.expense.periodMissing')
            }
          />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="entryDate"
              label={t('finance.expense.date')}
              extra={t('finance.expense.dateHint')}
              rules={[{ required: true, message: t('finance.expense.dateRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="amount"
              label={t('finance.expense.amount')}
              extra={t('finance.expense.amountHint')}
              rules={[
                { required: true, message: t('finance.expense.amountRequired') },
                {
                  validator: (_rule, value: Money | null) =>
                    value && value.amount > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('finance.expense.amountPositive'))),
                },
              ]}
            >
              {/* ⚠️ Major↔minor aylantirish FAQAT shu komponent ichida */}
              <MoneyInput currency={currency} minMinor={1} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="expenseAccountId"
              label={t('finance.expense.expenseAccount')}
              extra={t('finance.expense.expenseAccountHint')}
              rules={[{ required: true, message: t('finance.expense.expenseAccountRequired') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={expenseOptions}
                loading={accounts.isPending}
                notFoundContent={
                  accountsReliable ? t('finance.expense.noExpenseAccounts') : t('common.loading')
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="creditAccountId"
              label={t('finance.expense.creditAccount')}
              extra={t('finance.expense.creditAccountHint')}
              dependencies={['expenseAccountId']}
              rules={[
                { required: true, message: t('finance.expense.creditAccountRequired') },
                ({ getFieldValue }) => ({
                  validator: (_rule, value: string | undefined) =>
                    // ⚠️ Server ham rad etadi ("bir xil bo'la olmaydi"),
                    // lekin oldindan aytish bekor urinishdan saqlaydi.
                    value && value === getFieldValue('expenseAccountId')
                      ? Promise.reject(new Error(t('finance.expense.sameAccount')))
                      : Promise.resolve(),
                }),
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={creditOptions}
                loading={accounts.isPending}
                notFoundContent={
                  accountsReliable ? t('finance.expense.noCreditAccounts') : t('common.loading')
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="orgNodeId"
          label={t('finance.expense.costCenter')}
          extra={
            dimensionRequired
              ? t('finance.expense.costCenterRequiredHint')
              : t('finance.expense.costCenterHint')
          }
          dependencies={['expenseAccountId']}
          rules={
            dimensionRequired
              ? [{ required: true, message: t('finance.expense.costCenterRequired') }]
              : []
          }
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={centerOptions}
            loading={costCenters.isPending}
            placeholder={t('finance.expense.costCenterNone')}
            notFoundContent={
              costCenters.isError
                ? translateError(costCenters.error, t)
                : centersReliable
                  ? t('finance.expense.noCostCenters')
                  : t('common.loading')
            }
          />
        </Form.Item>

        {/* ⚠️ Bo'sh ro'yxat — XATO EMAS, ma'lumot yo'qligi (§12.5).
            Operatorga nima qilish kerakligi aytiladi. */}
        {centersReliable && centerOptions.length === 0 ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('finance.expense.noCostCentersTitle')}
            description={t('finance.expense.noCostCentersBody')}
          />
        ) : null}

        <Form.Item
          name="memo"
          label={t('finance.expense.memo')}
          extra={t('finance.expense.memoHint')}
          rules={[{ max: MEMO_MAX, message: t('finance.expense.memoTooLong') }]}
        >
          <Input.TextArea rows={2} maxLength={MEMO_MAX} showCount />
        </Form.Item>

        <Typography.Text type="secondary">{t('finance.expense.immutableNote')}</Typography.Text>
      </Form>
    </Modal>
  )
}
